import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  keyframes,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';
import { Routes } from '../../../routes';
import { PageHeader } from '../../../shared/components/layout/PageHeader';
import { OnboardingChecklist } from '../components/OnboardingChecklist';
import { useOnboardingStatus } from '../hooks/useOnboardingStatus';
import { useCompleteOnboarding } from '../hooks/useCompleteOnboarding';
import { useMultiAccountStore } from '../../auth/stores/multiAccountStore';
import {
  getCompletedCount,
  isAllComplete,
  TOTAL_STEPS,
} from '../utils/onboardingSteps';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeSlideUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const GettingStartedPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: status, isLoading } = useOnboardingStatus();
  const { mutate: complete, isPending } = useCompleteOnboarding();
  const orgName = useMultiAccountStore(
    s => s.getCurrentAccount()?.currentOrganisationName
  );

  const completedSteps = status ? getCompletedCount(status) : 0;
  const totalSteps = TOTAL_STEPS;
  const allComplete = status ? isAllComplete(status) : false;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title="Getting Started"
        data-testid="getting-started-header"
      />

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 600,
            px: { xs: 2, sm: 3 },
            py: { xs: 2.5, sm: 4 },
          }}
        >
          {/* Welcome + progress */}
          <Box
            sx={{
              mb: { xs: 3, sm: 4 },
              opacity: 0,
              animation: `${fadeIn} 0.5s ease-out 0.1s forwards`,
            }}
            data-testid="getting-started-welcome"
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 1,
              }}
            >
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, flex: 1, letterSpacing: '-0.01em' }}
              >
                Welcome{orgName ? `, ${orgName}` : ''}!
              </Typography>

              {/* Circular progress ring */}
              {status && (
                <Box
                  sx={{ position: 'relative', display: 'inline-flex' }}
                  data-testid="getting-started-progress"
                >
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={44}
                    thickness={3}
                    sx={{ color: 'action.selected' }}
                  />
                  <CircularProgress
                    variant="determinate"
                    value={(completedSteps / totalSteps) * 100}
                    size={44}
                    thickness={3}
                    sx={{
                      position: 'absolute',
                      left: 0,
                      color: allComplete ? 'success.main' : 'primary.main',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        color: allComplete ? 'success.main' : 'text.primary',
                      }}
                    >
                      {completedSteps}/{totalSteps}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 440, lineHeight: 1.6 }}
            >
              Complete these steps to get your organisation set up and ready to
              go.
            </Typography>
          </Box>

          {isLoading ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center', py: 6 }}
              data-testid="getting-started-loading"
            >
              <CircularProgress />
            </Box>
          ) : status ? (
            <>
              <OnboardingChecklist status={status} />

              <Box
                sx={{
                  mt: 4,
                  display: 'flex',
                  justifyContent: 'center',
                  opacity: 0,
                  animation: `${fadeSlideUp} 0.4s ease-out 0.9s forwards`,
                }}
              >
                <Button
                  variant={allComplete ? 'contained' : 'text'}
                  onClick={() =>
                    complete(undefined, {
                      onSuccess: () =>
                        navigate(Routes.sites.manage, { replace: true }),
                    })
                  }
                  disabled={isPending}
                  endIcon={
                    allComplete ? (
                      <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <ArrowForwardIcon sx={{ fontSize: 16 }} />
                    )
                  }
                  sx={{
                    ...(!allComplete && {
                      color: 'text.secondary',
                      '&:hover': { color: 'text.primary' },
                    }),
                  }}
                  data-testid="onboarding-dismiss-button"
                >
                  {isPending
                    ? 'Completing...'
                    : allComplete
                      ? 'All done — go to my sites'
                      : 'Skip for now'}
                </Button>
              </Box>
            </>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default GettingStartedPage;
